import React, { useState, useRef, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    StatusBar,
    Platform,
    Image,
    Switch,
    Modal,
    TextInput,
    Animated,
    ActivityIndicator,
    KeyboardAvoidingView,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import SimpleHeader from '../../components/SimpleHeader';
import { useAuth } from '../../contexts/AuthContext';
import { usePIN } from '../../contexts/PINContext';
import { useCapabilities } from '../../hooks/useCapabilities';
import BackButton from '../../components/UIComponents/BackButton';
import PINDots from '../../components/UIComponents/PINDots';
import PinKeypad, { PIN_INPUT_PROPS } from '../../../web/PinKeypad';
import { COLORS } from '../../theme/themeTokens';

type TabScreen = 'dashboard' | 'products' | 'reports' | 'support' | 'profile' | 'userSessions';

interface ProfileScreenProps {
    onBack: () => void;
    onNavigate: (screen: TabScreen) => void;
    onChangePassword: () => void;
    onChangePIN?: () => void;
}

const ProfileScreen: React.FC<ProfileScreenProps> = ({ onBack, onNavigate, onChangePassword, onChangePIN }) => {
    // Status-bar inset for Android — same value CardScreen uses so
    // the title sits at the same vertical position. iOS gets 0
    // because the App-level SafeAreaView already handles the notch.
    const statusBarHeight = Platform.OS === 'android' ? 28 : 0;
    const { user, biometricsEnabled, enableBiometrics, disableBiometrics, logout, impersonate, refreshUser } = useAuth();
    const { isPINSetup, clearPIN, verifyPIN } = usePIN();
    const caps = useCapabilities();

    // Re-fetch /auth/me on every mount of this screen. Cheap, and means the
    // settings page never shows stale role/capability state — for example,
    // when a DBA has just flipped is_super_user for this user.
    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const [showPINModal, setShowPINModal] = useState(false);
    const [modalPIN, setModalPIN] = useState('');
    const [modalError, setModalError] = useState<string | null>(null);
    const modalInputRef = useRef<TextInput>(null);
    const shakeAnimation = useRef(new Animated.Value(0)).current;

    // Impersonation (super-user only) modal state
    const [showImpModal, setShowImpModal] = useState(false);
    const [impTargetLogin, setImpTargetLogin] = useState('');
    const [impPassword, setImpPassword] = useState('');
    const [impReason, setImpReason] = useState('');
    const [impBusy, setImpBusy] = useState(false);
    const [impError, setImpError] = useState<string | null>(null);

    const userLogin = user?.login || '-----';
    const userCode = user?.external_id ? String(user.external_id) : '--------';

    const handlePINToggle = async (value: boolean) => {
        if (value && !isPINSetup) {
            // Enabling PIN - navigate to PIN setup screen
            if (onChangePIN) {
                onChangePIN();
            }
        } else if (!value && isPINSetup) {
            // Disabling PIN - show PIN verification modal
            setModalPIN('');
            setModalError(null);
            setShowPINModal(true);
        }
    };

    const shakeModal = () => {
        Animated.sequence([
            Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
            Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true }),
        ]).start();
    };

    const handleModalPINChange = (value: string) => {
        const digits = value.replace(/[^0-9]/g, '').slice(0, 4);
        setModalError(null);
        setModalPIN(digits);

        if (digits.length === 4) {
            setTimeout(async () => {
                const isValid = await verifyPIN(digits);
                if (isValid) {
                    setShowPINModal(false);
                    setModalPIN('');
                    // PIN verified - disable biometrics and clear PIN
                    if (biometricsEnabled) {
                        await disableBiometrics();
                    }
                    await clearPIN();
                } else {
                    shakeModal();
                    setModalError('Wrong PIN. Try again.');
                    setModalPIN('');
                }
            }, 200);
        }
    };

    const handleModalClose = () => {
        setShowPINModal(false);
        setModalPIN('');
        setModalError(null);
    };

    const handleCopyCode = () => {
        if (user?.id) {
            Clipboard.setString(user.id);
        }
    };

    const handleBiometricToggle = async (value: boolean) => {
        if (value) {
            await enableBiometrics();
        } else {
            await disableBiometrics();
        }
    };

    const handleLogout = async () => {
        await logout();
    };

    const openImpModal = () => {
        setImpTargetLogin('');
        setImpPassword('');
        setImpReason('');
        setImpError(null);
        setImpBusy(false);
        setShowImpModal(true);
    };

    const closeImpModal = () => {
        if (impBusy) return;
        setShowImpModal(false);
    };

    const handleImpersonateSubmit = async () => {
        const target = impTargetLogin.trim();
        const pwd = impPassword;
        if (!target) {
            setImpError('Enter target login');
            return;
        }
        if (!pwd) {
            setImpError('Enter your password');
            return;
        }
        setImpBusy(true);
        setImpError(null);
        try {
            await impersonate(target, pwd, impReason.trim() || undefined);
            // Success — close the modal. The yellow ImpersonationBanner in the
            // root layout will now be visible on every screen.
            setShowImpModal(false);
            setImpTargetLogin('');
            setImpPassword('');
            setImpReason('');
        } catch (e: any) {
            const msg = typeof e?.message === 'string' ? e.message : 'Impersonation failed';
            setImpError(msg);
        } finally {
            setImpBusy(false);
        }
    };

    return (
        <View style={styles.container}>
            {/* Android-only status-bar inset — see comment on
                statusBarHeight above. iOS gets 0, so the wrapper is a
                no-op there. */}
            <View style={{ paddingTop: statusBarHeight, backgroundColor: COLORS.white }}>
                <SimpleHeader title="Settings" onBack={onBack} backgroundVariant="white" />
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}>

                    {/* Login row — first in the list */}
                    <View style={[styles.settingsRow, { marginBottom: 10 }]}>
                        <View style={styles.settingsIconContainer}>
                            <Image
                                source={require('../../assets/user.png')}
                                style={styles.settingsIcon}
                                resizeMode="contain"
                            />
                        </View>
                        <View style={styles.settingsContent}>
                            <Text style={styles.settingsTitle}>Login</Text>
                            <Text style={styles.settingsSubtitle}>{user?.login ?? '—'}</Text>
                        </View>
                    </View>

                    {/* Divider after Login */}
                    <View style={styles.loginDivider} />

                    {/* Face ID / Touch ID — hidden under impersonation (device-level setting
                        of the super-user, must not be toggled while logged in as someone else).
                        Not on the web: the browser has no Face ID / Touch ID for the app (passkeys later). */}
                    {Platform.OS !== 'web' && !user?.is_impersonating && (
                        <View style={[styles.settingsRow, !isPINSetup && styles.settingsRowDisabled]}  >
                            <View style={styles.settingsIconContainer}>
                                <Image
                                    source={require('../../assets/profile-faceid.png')}
                                    style={[styles.settingsIcon, !isPINSetup && styles.settingsIconDisabled]}
                                    resizeMode="contain"
                                />
                            </View>
                            <View style={styles.settingsContent}>
                                <Text style={[styles.settingsTitle, !isPINSetup && styles.settingsTitleDisabled]}>Face ID / Touch ID</Text>
                                <Text style={[styles.settingsSubtitle, !isPINSetup && styles.settingsSubtitleDisabled]}>
                                    {isPINSetup ? 'Manage your device security' : 'Enable PIN to use biometrics'}
                                </Text>
                            </View>
                            <Switch
                                value={biometricsEnabled}
                                onValueChange={handleBiometricToggle}
                                disabled={!isPINSetup}
                                trackColor={{ false: COLORS.softGrey, true: COLORS.steelNavy }}
                                thumbColor={biometricsEnabled ? COLORS.white : COLORS.blueSteel}
                                style={styles.toggle}
                            />
                        </View>
                    )}
                    {/* Change Password — gated by server capability (can_change_password). */}
                    {caps.can_change_password && (
                        <TouchableOpacity style={styles.settingsRow} onPress={onChangePassword}>
                            <View style={styles.settingsIconContainer}>
                                <Image source={require('../../assets/profile-change-password.png')} style={styles.settingsIcon} resizeMode="contain" />
                            </View>
                            <View style={styles.settingsContent}>
                                <Text style={styles.settingsTitle}>Change password</Text>
                                <Text style={styles.settingsSubtitle}>Change your account password</Text>
                            </View>
                            <Text style={styles.chevron}>›</Text>
                        </TouchableOpacity>
                    )}

                    {/* Change PIN — hidden under impersonation (device-level setting). */}
                    {!user?.is_impersonating && (
                        <TouchableOpacity style={styles.settingsRow} onPress={onChangePIN}>
                            <View style={styles.settingsIconContainer}>
                                <Image source={require('../../assets/profile-change-password.png')} style={styles.settingsIcon} resizeMode="contain" />
                            </View>
                            <View style={styles.settingsContent}>
                                <Text style={styles.settingsTitle}>Change PIN</Text>
                                <Text style={styles.settingsSubtitle}>Change your account PIN</Text>
                            </View>
                            <Text style={styles.chevron}>›</Text>
                        </TouchableOpacity>
                    )}

                    {/* Two-Factor Authentication */}
                    {/* <TouchableOpacity style={styles.settingsRow} onPress={() => console.log('2FA')}>
                        <View style={styles.settingsIconContainer}>
                            <Image source={require('../../assets/profile-two-factor.png')} style={styles.settingsIcon} resizeMode="contain" />
                        </View>
                        <View style={styles.settingsContent}>
                            <Text style={styles.settingsTitle}>Two-Factor Authentication</Text>
                            <Text style={styles.settingsSubtitle}>Further secure your account for safety</Text>
                        </View>
                        <Text style={styles.chevron}>›</Text>
                    </TouchableOpacity> */}

                    {/* Login as user — super-user only */}
                    {user?.is_super_user === true && !user?.is_impersonating && (
                        <TouchableOpacity style={styles.settingsRow} onPress={openImpModal}>
                            <View style={styles.settingsIconContainer}>
                                <Image source={require('../../assets/profile-change-password.png')} style={styles.settingsIcon} resizeMode="contain" />
                            </View>
                            <View style={styles.settingsContent}>
                                <Text style={styles.settingsTitle}>Login as user</Text>
                                <Text style={styles.settingsSubtitle}>Open another user's account (audited)</Text>
                            </View>
                            <Text style={styles.chevron}>›</Text>
                        </TouchableOpacity>
                    )}

                    {/* User sessions — super-user only. Hidden under impersonation
                        because while impersonating, "you" are not the super-user
                        viewing the audit log — you're the target user. */}
                    {user?.is_super_user === true && !user?.is_impersonating && (
                        <TouchableOpacity style={styles.settingsRow} onPress={() => onNavigate('userSessions')}>
                            <View style={styles.settingsIconContainer}>
                                <Image source={require('../../assets/profile-change-password.png')} style={styles.settingsIcon} resizeMode="contain" />
                            </View>
                            <View style={styles.settingsContent}>
                                <Text style={styles.settingsTitle}>User sessions</Text>
                                <Text style={styles.settingsSubtitle}>See who's using the app right now</Text>
                            </View>
                            <Text style={styles.chevron}>›</Text>
                        </TouchableOpacity>
                    )}

                    {/* Log out */}
                    <TouchableOpacity style={[styles.settingsRow, { marginBottom: 10 }]} onPress={handleLogout}>
                        <View style={styles.settingsIconContainer}>
                            <Image source={require('../../assets/profile-logout.png')} style={styles.settingsIcon} resizeMode="contain" />
                        </View>
                        <View style={styles.settingsContent}>
                            <Text style={styles.settingsTitle}>Log out</Text>
                            <Text style={styles.settingsSubtitle}>Log out of account</Text>
                        </View>
                        <Text style={styles.chevron}>›</Text>
                    </TouchableOpacity>

                    {/* Divider before Reports */}
                    <View style={styles.loginDivider} />

                    {/* Reports */}
                    <TouchableOpacity style={styles.settingsRow} onPress={() => onNavigate('reports')}>
                        <View style={styles.settingsIconContainer}>
                            <Image
                                source={require('../../assets/products/diagram.png')}
                                style={styles.settingsIcon}
                                resizeMode="contain"
                            />
                        </View>
                        <View style={styles.settingsContent}>
                            <Text style={styles.settingsTitle}>Reports</Text>
                            <Text style={styles.settingsSubtitle}>View your account reports</Text>
                        </View>
                        <Text style={styles.chevron}>›</Text>
                    </TouchableOpacity>
                </ScrollView>

            </View>

            {/* PIN Verification Modal */}
            <Modal
                visible={showPINModal}
                transparent
                animationType="fade"
                onRequestClose={handleModalClose}
                onShow={() => {
                    // Reliable focus after modal animation completes
                    setTimeout(() => {
                        modalInputRef.current?.focus();
                    }, 100);
                }}
            >
                <TouchableOpacity
                    style={styles.modalOverlay}
                    activeOpacity={1}
                    onPress={handleModalClose}
                >
                    <TouchableOpacity activeOpacity={1} style={styles.modalContainer}>
                        <Text style={styles.modalTitle}>Enter current PIN</Text>
                        <Text style={styles.modalSubtitle}>Confirm your PIN to disable it</Text>

                        <TextInput
                            ref={modalInputRef}
                            value={modalPIN}
                            onChangeText={handleModalPINChange}
                            keyboardType="number-pad"
                            maxLength={4}
                            style={styles.hiddenInput}
                            caretHidden
                            {...PIN_INPUT_PROPS}
                        />

                        <PINDots
                            pin={modalPIN}
                            pinLength={4}
                            error={modalError}
                            shakeAnimation={shakeAnimation}
                            onPress={() => {
                                modalInputRef.current?.blur();
                                setTimeout(() => modalInputRef.current?.focus(), 50);
                            }}
                        />

                        {modalError && <Text style={styles.modalError}>{modalError}</Text>}

                        <PinKeypad value={modalPIN} length={4} onChange={handleModalPINChange} compact />

                        <TouchableOpacity style={styles.modalCancelButton} onPress={handleModalClose}>
                            <Text style={styles.modalCancelText}>Cancel</Text>
                        </TouchableOpacity>
                    </TouchableOpacity>
                </TouchableOpacity>
            </Modal>

            {/* Impersonation Modal (super-user only) */}
            <Modal
                visible={showImpModal}
                transparent
                animationType="fade"
                onRequestClose={closeImpModal}
            >
                <TouchableOpacity
                    style={styles.modalOverlay}
                    activeOpacity={1}
                    onPress={closeImpModal}
                >
                    <KeyboardAvoidingView
                        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                        style={styles.impKbAvoider}
                    >
                        <TouchableOpacity activeOpacity={1} style={[styles.modalContainer, styles.impModalContainer]}>
                            <Text style={styles.modalTitle}>Login as user</Text>
                            <Text style={styles.modalSubtitle}>
                                Enter the target user's login and your own password. This action is audited.
                            </Text>

                            <TextInput
                                value={impTargetLogin}
                                onChangeText={(v) => { setImpTargetLogin(v); setImpError(null); }}
                                placeholder="Target login"
                                placeholderTextColor={COLORS.blueSteel}
                                autoCapitalize="none"
                                autoCorrect={false}
                                editable={!impBusy}
                                style={styles.impInput}
                            />
                            <TextInput
                                value={impPassword}
                                onChangeText={(v) => { setImpPassword(v); setImpError(null); }}
                                placeholder="Your password"
                                placeholderTextColor={COLORS.blueSteel}
                                secureTextEntry
                                autoCapitalize="none"
                                autoCorrect={false}
                                editable={!impBusy}
                                style={styles.impInput}
                            />
                            <TextInput
                                value={impReason}
                                onChangeText={setImpReason}
                                placeholder="Reason (optional)"
                                placeholderTextColor={COLORS.blueSteel}
                                editable={!impBusy}
                                style={styles.impInput}
                            />

                            {impError && <Text style={styles.modalError}>{impError}</Text>}

                            <View style={styles.impButtonRow}>
                                <TouchableOpacity
                                    style={[styles.modalCancelButton, impBusy && styles.impDisabledBtn]}
                                    onPress={closeImpModal}
                                    disabled={impBusy}
                                >
                                    <Text style={styles.modalCancelText}>Cancel</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[styles.impSubmitBtn, impBusy && styles.impDisabledBtn]}
                                    onPress={handleImpersonateSubmit}
                                    disabled={impBusy}
                                >
                                    {impBusy ? (
                                        <ActivityIndicator size="small" color={COLORS.white} />
                                    ) : (
                                        <Text style={styles.impSubmitText}>Login as</Text>
                                    )}
                                </TouchableOpacity>
                            </View>
                        </TouchableOpacity>
                    </KeyboardAvoidingView>
                </TouchableOpacity>
            </Modal>


        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.white,
    },
    headerBackground: {
        backgroundColor: COLORS.pureLight,
        paddingBottom: 12,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
    },
    backBtn: {
        width: 40,
        height: 40,
        justifyContent: 'center',
    },
    backArrow: {
        width: 20,
        height: 20,
        tintColor: COLORS.deepNavy,
    },
    headerTitle: {
        flex: 1,
        fontFamily: 'Inter',
        fontSize: 17,
        fontWeight: '600',
        color: COLORS.deepNavy,
        textAlign: 'center',
    },
    blurLeft: {
        position: 'absolute',
        width: 208,
        height: 208,
        left: -64,
        top: 100,
    },
    blurRight: {
        position: 'absolute',
        width: 208,
        height: 208,
        right: -64,
        top: 100,
    },
    userCardContainer: {
        paddingHorizontal: 20,
        alignItems: 'center',
    },
    userCard: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        maxWidth: 388,
        height: 90,
        backgroundColor: COLORS.brandOrange,
        borderRadius: 12,
        paddingHorizontal: 20,
        gap: 16,
        overflow: 'hidden',
        position: 'relative',
    },
    redBlurLeft: {
        position: 'absolute',
        width: 171,
        height: 171,
        left: -74,
        top: -63,
    },
    redBlurRight: {
        position: 'absolute',
        width: 171,
        height: 171,
        right: -81,
        top: '50%',
        marginTop: -85.5 + 44.5, // calc(50% - 171px/2 + 44.5px)
    },
    avatarCircle: {
        width: 50,
        height: 50,
        borderRadius: 25,
        backgroundColor: COLORS.white,
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarIcon: {
        width: 28,
        height: 28,
        tintColor: COLORS.blueSteel,
    },
    userInfo: {
        gap: 4,
    },
    userId: {
        fontSize: 22,
        fontWeight: '600',
        color: COLORS.white,
    },
    userIdRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    userIdSecondary: {
        fontSize: 16,
        color: COLORS.white,
        opacity: 0.8,
    },
    copyIcon: {
        width: 14,
        height: 14,
        tintColor: COLORS.white,
        opacity: 0.8,
    },
    mainContent: {
        flex: 1,
        backgroundColor: COLORS.white,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: 20,
        paddingBottom: 120,
    },
    settingsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 39,
        marginBottom: 20,
    },
    settingsIconContainer: {
        width: 24,
        height: 24,
        marginRight: 16,
    },
    settingsIcon: {
        width: 24,
        height: 24,
        tintColor: COLORS.steelNavy,
    },
    settingsContent: {
        flex: 1,
        flexDirection: 'column',
        gap: 3,
    },
    settingsTitle: {
        fontFamily: 'Inter',
        fontSize: 16,
        fontWeight: '500',
        lineHeight: 22,
        color: COLORS.deepNavy,
    },
    settingsSubtitle: {
        fontFamily: 'Inter',
        fontSize: 14,
        fontWeight: '400',
        lineHeight: 18,
        color: COLORS.blueSteel,
    },
    loginDivider: {
        height: 1,
        backgroundColor: COLORS.blueSteel,
        opacity: 0.25,
        marginTop: 20,
        marginBottom: 20,
    },
    toggle: {
        width: 51,
        height: 30,
    },
    chevron: {
        fontSize: 26,
        color: COLORS.blueSteel,
    },
    reportsButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.primaryNavy,
        height: 48,
        paddingHorizontal: 16,
        borderRadius: 12,
        marginHorizontal: 20,
        marginBottom: 8,
    },
    reportsIcon: {
        width: 24,
        height: 24,
        marginRight: 12,
    },
    reportsArrowIcon: {
        width: 20,
        height: 20,
        marginLeft: 'auto',
        tintColor: COLORS.white,
        transform: [{ rotate: '180deg' }],
    },
    reportsText: {
        fontFamily: 'Inter',
        fontSize: 18,
        fontWeight: '600',
        lineHeight: 19,
        color: COLORS.white,
    },
    userLoginLabel: {
        fontFamily: 'Inter',
        fontSize: 14,
        fontWeight: '400',
        color: COLORS.blueSteel,
        textAlign: 'center',
        marginTop: 8,
        marginBottom: Platform.OS === 'android' ? 64 : 24,
    },
    settingsRowDisabled: {
        opacity: 0.4,
    },
    settingsIconDisabled: {
        tintColor: COLORS.blueSteel,
    },
    settingsTitleDisabled: {
        color: COLORS.blueSteel,
    },
    settingsSubtitleDisabled: {
        color: COLORS.blueSteel,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContainer: {
        backgroundColor: COLORS.pureLight,
        borderRadius: 16,
        padding: 24,
        width: '85%',
        alignItems: 'center',
    },
    modalTitle: {
        fontFamily: 'Inter',
        fontSize: 20,
        fontWeight: '600',
        color: COLORS.deepNavy,
        marginBottom: 8,
    },
    modalSubtitle: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.blueSteel,
        marginBottom: 24,
    },
    modalError: {
        fontFamily: 'Inter',
        fontSize: 15,
        color: '#E53935',
        marginTop: -8,
        marginBottom: 8,
    },
    modalCancelButton: {
        marginTop: 8,
        paddingVertical: 10,
        paddingHorizontal: 24,
    },
    modalCancelText: {
        fontFamily: 'Inter',
        fontSize: 16,
        fontWeight: '500',
        color: COLORS.blueSteel,
    },
    hiddenInput: {
        position: 'absolute',
        opacity: 0,
        height: 0,
        width: 0,
    },
    impKbAvoider: {
        width: '100%',
        alignItems: 'center',
    },
    impModalContainer: {
        alignItems: 'stretch',
        width: '88%',
    },
    impInput: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.deepNavy,
        borderWidth: 1,
        borderColor: COLORS.softGrey,
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: Platform.OS === 'ios' ? 12 : 8,
        marginBottom: 12,
        backgroundColor: COLORS.white,
    },
    impButtonRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 8,
        gap: 8,
    },
    impSubmitBtn: {
        backgroundColor: COLORS.primaryNavy,
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 10,
        minWidth: 110,
        alignItems: 'center',
        justifyContent: 'center',
    },
    impSubmitText: {
        fontFamily: 'Inter',
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.white,
    },
    impDisabledBtn: {
        opacity: 0.6,
    },
});

export default ProfileScreen;

